import {
  CSV_MIME,
  DOCX_MIME,
  EXCEL_MIME_SET,
  EXCEL_MIMES,
  PDF_MIME,
  XLS_MIME,
  XLSX_MIME,
} from "./mime-types";

/** What produced a RexSkillVersion snapshot. */
export const REX_SKILL_VERSION_CHANGE_TYPE_VALUES = ["created", "updated", "archived", "restored"] as const;

export type RexSkillVersionChangeType = (typeof REX_SKILL_VERSION_CHANGE_TYPE_VALUES)[number];

export const REX_OBJECT_TYPES = [
  "team_member",
  "job",
  "activity",
  "pay_rate_group",
  "union_rate",
  "pto_policy",
  "pay_schedule",
  "timesheet",
  "equipment_timesheet",
  "material_log",
  "timesheet_workflow_policy",
  "break_policy",
  "card_transaction_policy",
  "expense_reimbursement_policy",
  "time_off_request_policy",
  "expense_reimbursement",
  "permission_group",
  "role",
  "company_setting",
  "daily_report",
  "department",
  "location",
  "ledger_mapping",
  "ledger_account",
  "ledger_entry",
  "bank_transaction",
  "rex_skill",
  "holiday_schedule",
  "assignment",
  "equipment",
  "custom_earning_code",
  "rate_differential",
  "time_off_request",
  "change_request",
  "payroll",
  "allowance",
  "employee_benefit",
  "shareholder_benefit",
  "post_tax_deduction",
  "per_diem_rate",
  "piece_rate_table",
  "overtime_rule",
  "workers_comp_code",
  "workers_comp_group",
  "workplace",
  "card_transaction",
  "expense_card",
  "third_party_card",
  "onboarding_checklist",
  "crew",
  "certification",
  "certification_type",
  "class",
  "position_template",
  "job_posting",
  "termination_reason",
  "standard_classification",
  "tag",
  "trade",
  "custom_field",
  "expense_reimbursement_category",
  "card_transaction_category",
  "burden_rate",
  "filing",
  "offer_template",
  "offer_letter",
  "cost_type",
  "attendance_policy",
  "offboarding_checklist",
  "broadcast",
  "feed_post",
  "note",
  "work_order",
  "leave_type",
  "company_entity",
  "record_schedule",
  "vendor",
  "bill",
  "integration_sync",
  "custom_report",
  "warning",
] as const;
export type RexObjectType = (typeof REX_OBJECT_TYPES)[number];

// `label` is optional: a Code Mode `query` whose `select` dropped the resource's pill label field
// still yields a ref (built from the always-present `id`), so the object deep-links via the link
// text the model wrote — the FE resolves the pill from `type` + `id` and takes its display text from
// the markdown label. A present `label` is the canonical name and is what the bare-id sanitizer
// backstop needs to synthesize a `[label](rex:obj/<id>)` marker; without it that backstop can't fire.
// `archived` is carried when the referenced row is soft-deleted, so the FE renders it as static
// (non-clickable) text — an archived object's page/list hides the row, so a link would go nowhere.
export type RexObjectRef = { type: RexObjectType; id: string; label?: string; archived?: boolean };

/** One sheet's handle envelope on a multi-sheet Excel attachment. Single-sheet workbooks and CSVs
 * keep the flat `handle` / `schema` / `total_count` / `preview` fields on the parent attachment. */
export type RexAttachmentSheetHandle = {
  sheet_name: string;
  handle: string;
  schema: Record<string, string>;
  total_count: number;
  preview: Array<Record<string, unknown>>;
};

/** Committed attachment on a user.message event. `file_id` resolves to a File doc with
 * `parent_type: "rex_conversation"`; the other fields are denormalized for cheap renders. */
export type RexUserMessageAttachment = {
  file_id: string;
  filename: string;
  mime: string;
  size: number;
  /** Tabular handle envelope (CSV or single-sheet Excel), populated by `hydrateAttachmentHandles`
   * at turn start. `handle` is the `_id` of the `attachment.data` RexEvent whose `cached_data` holds
   * the parsed rows; the model resolves it via `getHandle` inside `rex_exec_js`. `preview` is the
   * first ~3 rows. Multi-sheet Excel uses `sheet_handles` instead. Absent for non-tabular
   * attachments (image/pdf/text/markdown/docx), which are inlined into the prompt as before. */
  handle?: string;
  schema?: Record<string, string>;
  total_count?: number;
  preview?: Array<Record<string, unknown>>;
  /** Multi-sheet Excel only: one handle envelope per non-empty sheet. */
  sheet_handles?: RexAttachmentSheetHandle[];
};

/** Formats Rex can inline into the prompt (image/document content blocks or `<file>` text).
 * Shared so the FE can filter the picker and the BE can enforce on presign + commit. */
export const REX_ATTACHMENT_MIME_ALLOWLIST = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  PDF_MIME,
  "text/plain",
  "text/markdown",
  CSV_MIME,
  XLSX_MIME,
  XLS_MIME,
  DOCX_MIME,
] as const;
export type RexAttachmentMime = (typeof REX_ATTACHMENT_MIME_ALLOWLIST)[number];

export const REX_EXCEL_MIMES = EXCEL_MIMES satisfies readonly RexAttachmentMime[];
export const REX_EXCEL_MIME_SET: ReadonlySet<string> = EXCEL_MIME_SET;

export const REX_ATTACHMENT_MAX_FILE_BYTES = 10 * 1024 * 1024;
export const REX_ATTACHMENT_MAX_TOTAL_BYTES = 25 * 1024 * 1024;
export const REX_ATTACHMENT_MAX_PER_TURN = 15;

export const REX_EVENT_TYPES = [
  "user.message",
  "attachment.data",
  "turn.started",
  "turn.superseded",
  "agent.text",
  // Retired sticker art. Kept so the model's enum still accepts already-persisted events.
  "agent.sticker",
  "agent.thinking",
  "agent.tool_use",
  "agent.tool_use_start",
  "agent.tool_result",
  "agent.handle_data",
  "agent.model_call",
  "turn.idle",
  "turn.completed",
  "turn.errored",
  "turn.interrupted",
] as const;
export type RexEventType = (typeof REX_EVENT_TYPES)[number];

export const REX_TERMINAL_EVENT_TYPES = [
  "turn.completed",
  "turn.errored",
  "turn.interrupted",
] as const satisfies readonly RexEventType[];
export type RexTerminalEventType = (typeof REX_TERMINAL_EVENT_TYPES)[number];

/**
 * Which terminal path produced a `turn.errored`. These already existed as the LangFuse `statusMessage`
 * codes at each emission site; on the payload they let clients branch on the cause instead of
 * pattern-matching user-facing copy. `process_shutdown` is the one clients act on automatically — a
 * rolling deploy aborted the turn rather than it failing on its merits, so it's safe to resume.
 */
export const REX_TURN_ERROR_REASONS = [
  /** AppCleanup aborted the turn on SIGTERM (rolling deploy) or an uncaught exception. */
  "process_shutdown",
  /** The in-band watchdog fired because the model stream went silent. */
  "stream_idle_timeout",
  /** The out-of-band janitor cron swept a turn whose process died without writing a terminal. */
  "janitor_reaped",
  /** The prompt exceeded the model's context window even after compression. Not retryable. */
  "context_budget_exceeded",
  /** The model finished but its confirmation bundle failed to persist. */
  "bundle_persist_failed",
  /** The stream ended without a single model step. */
  "zero_turns",
  /** Conversation predates the current engine and can't be continued. Not retryable. */
  "legacy_conversation",
  /** The runner failed to launch, so the turn never started. */
  "runner_launch_failed",
  /** The turn outran the wall-clock budget its caller set. Programmatic invocations only. */
  "turn_budget_exceeded",
  /** Any other throw inside the agent loop. */
  "agent_error",
] as const;
export type RexTurnErrorReason = (typeof REX_TURN_ERROR_REASONS)[number];

export const REX_TURN_OUTCOMES = ["completed", "errored", "interrupted"] as const;
export type RexTurnOutcome = (typeof REX_TURN_OUTCOMES)[number];

/** Who is waiting on a turn. Nothing subscribes to a `programmatic` turn, so the terminal
 * notification and the per-delta event rows that exist to feed a live UI are wasted work. */
export const REX_TURN_SURFACES = ["interactive", "programmatic"] as const;
export type RexTurnSurface = (typeof REX_TURN_SURFACES)[number];

export const REX_EVENT_PERSISTENCE_MODES = ["full", "summary"] as const;
export type RexEventPersistenceMode = (typeof REX_EVENT_PERSISTENCE_MODES)[number];

/**
 * Which families of Rex's own tools a turn may reach. Derived per surface rather than declared by a
 * caller: an entry point knows what it is, and a tier name someone has to pick correctly is a tier
 * name someone picks wrong. Caller-supplied tools are merged after this filter and are never
 * withheld by it.
 */
export type RexToolSurface = {
  /** Company-data reads: the catalog, the by-domain read tools, skills, code mode. */
  reads: boolean;
  /** Confirmation-gated writes. Code mode's `callTool` answers to this too, since staging a write
   * from inside a script is still a write. */
  writes: boolean;
  /** Tools that reach a destination the model chooses (`fetch_url`, `search_web`). */
  egress: boolean;
  /** Tools answering with a downloadable artifact (`export_file`). */
  artifacts: boolean;
};

// Frozen because every turn shares the one object: narrowing a turn's surface by assigning to a
// flag would flip the constant for every later turn in the process. Build a new object instead.
export const REX_FULL_TOOL_SURFACE = Object.freeze<RexToolSurface>({
  reads: true,
  writes: true,
  egress: true,
  artifacts: true,
});

/**
 * Sentinel account for a turn no human is behind. Must stay a castable ObjectId: it lands in
 * `RexConversation.account_id`, which the mitotools reports feed into `_id: { $in: [...] }`. It
 * lives here rather than beside the Rex identity code because the audit-log model has to recognize
 * it, and models cannot import a Rex service without a cycle.
 */
export const REX_SYSTEM_ACCOUNT_ID = "000000000000000000000000";

/**
 * When a write tool's `apply` runs. `manual` stages every write into a confirmation bundle a human
 * approves, and the server applies the bundle afterwards. `auto` applies each write as the model
 * calls it, with no preview and no approval step. Defaults to `manual` everywhere: a turn that never
 * states a mode is a turn nobody decided to let write unattended.
 */
export const REX_WRITE_MODES = ["manual", "auto"] as const;
export type RexWriteMode = (typeof REX_WRITE_MODES)[number];

/** No Rex tools at all: whatever the caller supplied itself, and nothing else. */
export const REX_NO_TOOL_SURFACE = Object.freeze<RexToolSurface>({
  reads: false,
  writes: false,
  egress: false,
  artifacts: false,
});

/** Company-data reads and nothing else. */
export const REX_READ_ONLY_TOOL_SURFACE = Object.freeze<RexToolSurface>({
  reads: true,
  writes: false,
  egress: false,
  artifacts: false,
});

export type RexTurnToolCall = { name: string; ok: boolean };

/** One write applied inside a tool call, for a caller running a turn in process. Only auto mode
 * produces these: a staging turn writes nothing, and an approval applies through its own executor. */
export type RexTurnAppliedWrite = {
  tool: string;
  /** `partial`: some records in a batch landed and others did not. */
  status: "ok" | "partial" | "error";
  reason?: string;
  affected: Array<{ type: string; id?: string; label?: string }>;
};

/**
 * What one agent-loop invocation produced, for callers that run a turn and read its outcome in
 * process rather than subscribing to the event stream. Mirrors the terminal event the turn emitted.
 * `text` is the sanitized stream that reached `agent.text` — never the raw model output, which still
 * contains the database ids the sanitizer strips. `usage` counts only steps the provider reported
 * as finished, so an interrupted turn under-reports the tokens and cost it was billed for.
 */
export type RexTurnResult = {
  status: RexTurnOutcome;
  errorReason?: RexTurnErrorReason;
  text: string;
  toolCalls: RexTurnToolCall[];
  stepCount: number;
  usage: {
    inputTokens: number;
    outputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    /** USD the provider billed for this turn (OpenRouter credits, 1:1 with USD). */
    costUsd: number;
    /** The model the provider actually served (its reported id), which OpenRouter routing can make
     * differ from the requested one. Falls back to the requested model when no step reported one. */
    model: string;
  };
  stagedConfirmationToken?: string;
  /** Writes applied inside a tool call this turn. Populated on every outcome, since an interrupted
   * or timed-out auto turn can still have written before it stopped. */
  appliedWrites: RexTurnAppliedWrite[];
  /** Every object ref this turn surfaced (id -> {type,label,archived}), the same set fed to the pill
   * sanitizer. A caller persisting text/structured that still carry `[label](rex:obj/<id>)` markers
   * keeps these to resolve each marker into a deep-link pill later. */
  objectRefs: RexObjectRef[];
};

/**
 * A handle envelope returned by list-shaped tools. The tool persists `cached_data: { schema, rows }`
 * onto the `RexEvent` row whose `_id` is `handle`; the agent sees only this envelope. Subsequent
 * compute (`rex_exec_js`, future queries) resolves the handle and reads the cached rows server-side
 * — the full row array never enters the model's context.
 */
export type RexHandleEnvelope = {
  /** ObjectId string of the `agent.tool_result` event whose `cached_data` holds the row payload. */
  handle: string;
  /** Field name → primitive type label. Teaches the model which columns it can read off the rows. */
  schema: Record<string, string>;
  /** Field name → catalog description, for returned fields that have one. Carries the column
   * semantics the model would otherwise spend a describe_resource call on; kept out of `schema` so
   * that stays a pure type map machine consumers (e.g. the xlsx exporter) read verbatim. */
  field_descriptions?: Record<string, string>;
  total_count: number;
  /** True if the underlying query truncated; encourage the agent to narrow. */
  truncated?: boolean;
  /**
   * Opaque forage pagination key for the page after this one (null/absent when there are no more
   * rows). Pass it straight back as the next query's `cursor` to page — it encodes the active sort
   * fields, so it is the only correct way to paginate (never hand-build a cursor from a row id).
   */
  cursor?: string;
  /** Up to ~3 rows for the agent to eyeball without resolving the handle. */
  preview: Array<Record<string, unknown>>;
  /** Distinct jurisdictions in the full row set (e.g. `AR`, `MO`). Present on company tax tools
   * where preview truncation can hide states beyond the first page of rows — use this, not the
   * preview alone, when answering whether the company is registered in a state. */
  jurisdictions?: string[];
  /** Inline-citable references for the previewed rows, present only for resources that opt in (see
   * the catalog's `pill` config). Lets the FE resolve an `[label](rex:obj/<id>)` the agent writes
   * for a row it read here — without this the id never enters the turn's resolvable ref set. Built
   * from `preview`, so the agent can only cite rows it actually saw. */
  refs?: RexObjectRef[];
  /** Advisory when an implicit default filter narrowed this query, so a filtered-away row isn't read as absent. */
  notice?: string;
};

export type RexInstructionDiff = { label: string; before: string; after: string };

export type RexOutputSummary =
  | {
      kind: "question";
      questions: Array<{
        prompt: string;
        selection: "single" | "multiple";
        options: string[];
      }>;
    }
  | { kind: "object_ref"; ref: RexObjectRef }
  | {
      kind: "diff";
      instruction_preview_id?: string;
      instruction_diffs?: RexInstructionDiff[];
      changes: Array<{ field: string; old: unknown; new: unknown }>;
      // Set only when `changes` was capped (it then ends in a "…" row): the true count, and a handle
      // holding every row for export.
      total_changes?: number;
      preview_handle?: string;
      target: { type: RexObjectType; id?: string };
      confirmation_token: string;
      expires_at: string;
    }
  | {
      kind: "applied";
      // `partial` = bulk apply where some records succeeded (in `affected_resources`) and some failed
      // (in `failures`); `error` = nothing applied.
      status: "ok" | "partial" | "error";
      reason?: string;
      // `archived: true` marks a resource the action soft-deleted; the FE renders it as static text,
      // not a deep-link pill, since archive list views hide archived rows (the link goes nowhere).
      // `tab` is an optional sub-view hint the deep-link registry appends to the destination URL.
      affected_resources: Array<{
        type: RexObjectType;
        id?: string;
        label?: string;
        archived?: boolean;
        tab?: string;
        params?: Record<string, string>;
      }>;
      // Set only when `affected_resources` was capped (see bundle-apply `capAppliedSummary`): the true
      // total. Absent means the count is `affected_resources.length`.
      total_affected?: number;
      // Full per-record failures from a bulk (fan-out) apply. `reason` stays a short user-facing
      // summary; this untruncated list is model-facing (rendered only in the apply report) so the
      // model can explain or retry the exact failures. Not shown directly to the user.
      failures?: Array<{ ref: string; reason: string }>;
    }
  | {
      /** A confirmation-gated write that never reached the apply phase: its preview failed, or the
       *  submitted values already match what's stored, so nothing was staged and nothing ran. Kept
       *  distinct from `applied` with `status: "error"` — that one means an apply DID run and
       *  failed. Both mean no data changed, but only this one means no write was ever attempted,
       *  and a variant literally named "applied" reads to the model as a write that happened. */
      kind: "not_applied";
      reason: string;
      /** Set when the write was a no-op because the target already holds the requested values. */
      already_satisfied?: boolean;
      /** Records the failure points AT — e.g. the existing role that already holds the requested
       *  email. Nothing here was modified; they're carried so the reason can be linked. */
      affected_resources: Array<{
        type: RexObjectType;
        id?: string;
        label?: string;
        archived?: boolean;
        tab?: string;
        params?: Record<string, string>;
      }>;
    }
  | {
      /** A call that failed without touching data. NOT `applied` with `status: "error"` — that one means a write ran and failed, and only it earns a terminal "Apply failed" row under the turn. */
      kind: "error";
      reason: string;
    }
  | { kind: "list"; items: RexObjectRef[]; truncated?: boolean }
  | { kind: "handle"; envelope: RexHandleEnvelope }
  | {
      kind: "exec_result";
      result: unknown;
      logs: string[];
      error?: string;
      refs?: RexObjectRef[];
      /** Set on an inline result to tell the model it has no export handle and how to get one. */
      export_note?: string;
    }
  | {
      /** A generated, downloadable file artifact (e.g. a CSV export of a query result). The file is
       * a committed `File` row with `parent_type: "rex_conversation"`; the FE resolves a signed URL
       * via the same `/rex/attachments/:fileId/url` path user uploads use, so no extra plumbing. */
      kind: "download";
      file_id: string;
      filename: string;
      mime: string;
      size: number;
      /** Number of data rows in the file (excludes the header). Lets the FE show "120 rows". */
      row_count?: number;
    };

export type RexPageContext = {
  /**
   * Full client URL (path, query string, hash). Grounds the model on the current dashboard view;
   * query params and hash matter for some screens. Also useful alongside codebase indexing.
   */
  page_url: string;
  primary_entity?: RexObjectRef;
  filters?: Record<string, unknown>;
  selection?: Array<{ type: RexObjectType; id: string }>;
};

/** The user's picks for an `ask_user` question set, one list per question in order. */
export type RexQuestionAnswers = { tool_use_id: string; answers: string[][] };

export type RexEventBody =
  | {
      type: "user.message";
      payload: {
        text: string;
        page_context: RexPageContext;
        /** Set on approval turns: same string the BE emitted as confirmation_token on the prior
         * diff tool_result. Presence is the only signal — the FE skips rendering a chat bubble,
         * and the agent transcript builder skips the turn so the marker text never reaches the
         * model. Lets the FE derive "card has been approved" from events alone, so a refresh
         * during the apply round-trip doesn't re-enable the Approve button. */
        confirmation_token?: string;
        /** Set on a continuation turn: the id of the deploy-killed turn being resumed. Like
         * confirmation_token, presence tells the FE to skip the chat bubble — the user didn't write this.
         * Unlike confirmation_token, the `text` IS sent to the model, carrying the continuation instruction. */
        resume_of?: string;
        attachments?: RexUserMessageAttachment[];
        skill_id?: string;
        /** Set when this message answers an `ask_user` card. The FE renders the answers inside that
         * card instead of a chat bubble; `text` still carries them to the model. */
        question_answers?: RexQuestionAnswers;
        /** Set only for an `auto` turn, so its absence reads as `manual` on every turn written
         * before the mode existed. */
        write_mode?: RexWriteMode;
      };
    }
  // Server-only event minted at turn start for each uploaded tabular table (one per CSV, or one per
  // Excel sheet). Its `_id` is the handle the attachment envelope points at; the parsed rows ride on
  // `cached_data` ({ schema, rows }), which `appendRexEvent` persists to Mongo but never publishes
  // over the bus. The wire payload carries only the file identity (+ optional sheet_name) — the FE
  // reducer ignores this event kind (no render, no state change).
  | {
      type: "attachment.data";
      payload: { file_id: string; filename: string; sheet_name?: string };
    }
  // Server-only carrier for a handle a tool result referenced beyond the one on its own
  // `agent.tool_result` row (e.g. a rows handle inside an over-cap result). `_id` is the handle and
  // the payload rides on `cached_data`; the FE reducer ignores this event kind.
  | { type: "agent.handle_data"; payload: { tool_use_id: string } }
  | { type: "turn.started"; payload: { turn_id: string; started_at: string } }
  // Regenerate: the FE hides `superseded_turn_id` so this turn replaces it in place.
  | { type: "turn.superseded"; payload: { superseded_turn_id: string } }
  | { type: "agent.text"; payload: { delta: string } }
  | { type: "agent.thinking"; payload: { delta: string } }
  | {
      type: "agent.tool_use";
      payload: {
        tool: string;
        input: unknown;
        tool_use_id: string;
        /** Present-tense, user-facing label for the Rex step trail (e.g. "Looking up PTO
         * policy"). BE-owned and co-located with the tool definition (`rexTool`/`display`) so
         * the FE never maps tool names to prose. Falls back to a humanized tool name for tools
         * that predate `display`. Optional because events persisted before this field shipped
         * lack it; the FE reducer falls back when it's absent. */
        display_label?: string;
      };
    }
  | {
      /** Emitted on the SDK's `content_block_start` stream event when the model begins a tool
       * call. Carries only the tool name + id — the input args are still streaming. Lets the FE
       * render "Rex is preparing X..." immediately, instead of waiting for the full structured
       * input to finish generating (which for long tool calls like pto_create_policy is the bulk
       * of the call's latency). The matching `agent.tool_use` event still fires with the final
       * input when the assistant message completes. */
      type: "agent.tool_use_start";
      payload: { tool: string; tool_use_id: string; display_label?: string };
    }
  | {
      type: "agent.tool_result";
      payload: {
        tool_use_id: string;
        output_summary: RexOutputSummary;
        /** Past-tense, user-facing label for the completed step (e.g. "Looked up PTO policy").
         * Same source as `agent.tool_use_start.display_label`, swapped to the `done` phrasing.
         * Optional for the same back-compat reason as `agent.tool_use.display_label`. */
        display_label?: string;
      };
    }
  | {
      type: "agent.model_call";
      payload: {
        /** Model id the SDK reported on this assistant message. */
        model: string;
        /** SDK-assigned uuid for the assistant message — joins to the agent.text /
         * agent.tool_use events emitted alongside this call. */
        message_uuid: string;
        input_tokens: number;
        output_tokens: number;
        cache_read_input_tokens: number;
        cache_creation_input_tokens: number;
        /** Wall-clock from the previous SDK message yield (or iterator start) to this
         * assistant message yield. Total call latency from the agent loop's perspective. */
        elapsed_ms: number;
        /** Model-reported stop reason. `tool_use` for mid-turn structured calls,
         * `end_turn` for the closing summary call. */
        stop_reason: string | null;
      };
    }
  | { type: "turn.idle"; payload: { stop_reason: string } }
  // `pill_markers` counts the object pills the sanitizer had to unlink this turn: `unresolved` (the id
  // was never surfaced by a tool result) and `mismatched` (the id resolved, but to an entity whose
  // label contradicts the model's own link text). Both are silent degradations — the user sees plain
  // text where a deep link belonged — so they are recorded here to be gradeable. Optional: turns
  // written before the field existed, and the lifecycle path that completes a turn with no text, omit it.
  // `trace_id` (on every terminal type) is the turn's LangFuse trace id. It has to be persisted here:
  // LangFuse can't look a trace up by turn_id except via a windowed, rate-limited metadata search.
  // Absent when no OTel provider is registered (LANGFUSE_* unset), on events written before the field
  // existed, and on terminals written outside the agent loop (janitor reaps never flush a trace).
  // `fabricated_write_claim` records that the completion-claim guard fired: the ledger proved nothing
  // was written this turn, the reply claimed otherwise, and a retraction was appended. Recorded so the
  // rate is gradeable and queryable rather than only visible as an appended paragraph. Optional for the
  // same reasons `pill_markers` is.
  // `confirmation_expires_at` is the real expiry of the bundle this turn staged, set when the bundle is
  // persisted at turn end. It supersedes the provisional `expires_at` on the turn's diff tool results.
  | {
      type: "turn.completed";
      payload: {
        usage: Record<string, number>;
        pill_markers?: { unresolved: number; mismatched: number };
        fabricated_write_claim?: boolean;
        confirmation_expires_at?: string;
        trace_id?: string;
      };
    }
  // `reason` is optional only for events written before the field existed; every emission site sets it
  // now. Read sites should treat `undefined` as "unknown cause", not as a reason to retry.
  | {
      type: "turn.errored";
      payload: { message: string; retryable: boolean; reason?: RexTurnErrorReason; trace_id?: string };
    }
  // User-initiated stop. The FE suppresses the turn's agent bubble, so there's no payload to render.
  | { type: "turn.interrupted"; payload: { trace_id?: string } };

export const REX_CONFIRMATION_STATUSES = ["pending", "redeemed", "failed"] as const;
export type RexConfirmationStatus = (typeof REX_CONFIRMATION_STATUSES)[number];

export const REX_CONFIRMATION_FAILURE_CODES = [
  "confirm_token_invalid",
  "confirm_token_expired",
  "confirm_token_wrong_tool",
  "confirm_token_payload_drift",
  "confirm_token_session_mismatch",
] as const;
export type RexConfirmationFailureCode = (typeof REX_CONFIRMATION_FAILURE_CODES)[number];

export const REX_PILLAR_VALUES = [
  "payroll",
  "hcm",
  "field_ops",
  "platform",
  "ecosystem",
  "spend",
  "other",
] as const;
export type RexPillar = (typeof REX_PILLAR_VALUES)[number];
